import { motion } from "framer-motion";
import { useRef } from "react";
import { useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";

const IMG = "/images/palais-justice-rouen.jpg";

export function ChapterLabel({ num, title, dark = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className="flex items-baseline gap-4 mb-10"
    >
      <span className="font-serif text-brass text-xl md:text-2xl italic">{num}</span>
      <span className={`text-[11px] uppercase tracking-[0.35em] ${dark ? "text-paper/50" : "text-ink/50"}`}>{title}</span>
      <span className={`flex-1 h-px ${dark ? "bg-paper/10" : "bg-ink/10"}`} />
    </motion.div>
  );
}

export default function Expertise() {
  const ref = useRef(null);
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="expertise" ref={ref} data-testid="expertise-section" className="bg-paper py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <ChapterLabel num="01" title={t("expertise.label")} />
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink"
            >
              {t("expertise.titleA")} <em className="text-brass not-italic font-normal">{t("expertise.titleB")}</em>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-10 max-w-xl space-y-5 text-base leading-relaxed text-ink/70"
            >
              <p>
                {t("expertise.p1a")}<strong className="text-ink font-medium">{t("expertise.p1bold")}</strong>{t("expertise.p1b")}
              </p>
              <p>{t("expertise.p2")}</p>
            </motion.div>
            <motion.ul
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              data-testid="expertise-list"
              className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3"
            >
              {t("expertise.list").map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-ink/80">
                  <span className="w-1.5 h-1.5 bg-brass rotate-45 shrink-0" />
                  {item}
                </li>
              ))}
            </motion.ul>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -top-4 -left-4 w-full h-full border border-brass/40 pointer-events-none" />
            <div className="overflow-hidden aspect-[4/5] bg-slate2">
              <motion.img style={{ y: imgY }} src={IMG} alt="Palais de Justice de Rouen — Cour d'Appel" className="w-full h-[116%] object-cover" />
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-ink/40">{t("expertise.caption")}</p>
            <p className="mt-1.5 text-[10px] text-ink/30">
              Palais de Justice de Rouen — Photo :{" "}
              <a
                href="https://commons.wikimedia.org/wiki/File:Parlement_de_Normandie_Rouen_2009_02.jpg"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="expertise-photo-credit"
                className="underline decoration-ink/20 hover:text-brass transition-colors duration-300"
              >
                Wikimedia Commons
              </a>
              , licence CC BY-SA
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
